import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import SVG from 'components/base/SVG';
import { addListener, removeListener, isOnElement } from 'utils/DOM/onOuterClick';

import css from './style.scss';

// 20x20 Checkbox, Figma 2621:73420 — the glyph a multi-select row draws in
// place of the single-select row's teal fill. The tick is the design's own path
// rather than a re-struck stroke; the box itself is CSS, so it can key off the
// row without a second asset.
const Checkbox = ({ checked }) => (
  <span className={classNames(css.checkbox, { [css.checkboxChecked]: checked })}>
    {!checked ? null : (
      <svg className={css.checkbox__tick} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
        <path d="M18.0481 4.35151C18.5168 4.82018 18.5168 5.58132 18.0481 6.04999L8.44962 15.6485C7.98094 16.1172 7.21981 16.1172 6.75113 15.6485L1.95188 10.8492C1.48321 10.3806 1.48321 9.61943 1.95188 9.15076C2.42056 8.68208 3.18169 8.68208 3.65037 9.15076L7.60225 13.0989L16.3534 4.35151C16.8221 3.88283 17.5832 3.88283 18.0519 4.35151H18.0481Z" />
      </svg>
    )}
  </span>
);

// Filter dropdown — Figma nodes 291:9023 (closed) and 291:6050 (open).
// Closed: 1px Grey/200 border, 12px radius. Open: the control takes a 2px
// Teal/500 border with square bottom corners and the panel hangs off it with
// the matching border, so the two read as one shape.
//
// `multiple` draws each row with a checkbox instead of the single-select row's
// teal fill (Figma 2621:73418). An option marked `exclusive` — Investor Type's
// "Any" — stands for the EMPTY selection rather than for a value of its own.
// That one idea gives the designer's note both of its directions at once:
// picking "Any" clears the list, and picking any other option leaves the list
// non-empty, so "Any" deselects itself. The sentinel is never one of the values
// `onChange` reports, which is why `data/investors` keeps it out of
// `InvestorTypeOptions`.
const Dropdown = ({
  value,
  options,
  onChange,
  multiple = false,
  className,
  maxWidth,
  width,
  height,
  compact,
  placeholder = false,
  menuClassName,
  itemSelectedClassName,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleOuterClick = (ev) => {
      if (!isOnElement(ev, ref.current)) setOpen(false);
    };

    addListener(handleOuterClick);

    return () => removeListener(handleOuterClick);
  }, [open]);

  // An Immutable List in the filter row; `size` keeps this honest if a caller
  // ever passes a plain array.
  const selectionSize = () => (value && value.size !== undefined ? value.size : value.length);

  const isSelected = (option) => {
    if (!multiple) return value === option.value;

    return option.exclusive ? !selectionSize() : value.includes(option.value);
  };

  const handleSelect = (option) => () => {
    if (!multiple) {
      onChange(option.value);
      setOpen(false);
      return;
    }

    // Stays open: picking a type is not the end of a multi-select. `filter` and
    // `concat` rather than `clear`/`push`, so an array caller works too.
    if (option.exclusive) {
      onChange(value.filter(() => false));
      return;
    }

    onChange(
      isSelected(option) ? value.filter((v) => v !== option.value) : value.concat([option.value])
    );
  };

  const selected = options.filter(isSelected);
  // With an `exclusive` option in the list the empty case already resolves to
  // its label ("Any"); the fallback covers a multi-select that has none.
  const label = multiple
    ? !selected.length
      ? 'Any'
      : selected.map((o) => o.label).join(', ')
    : (selected[0] || {}).label;

  // Figma 295:6062 — an unset Min/Max reads as placeholder text, not as a
  // chosen value. Opt-in per control rather than inferred from an empty value:
  // Min Properties Purchased also has an empty option, but its "Any" IS the
  // chosen value and stays Grey/800.
  const showPlaceholder =
    placeholder && !multiple && (value === '' || value === null || value === undefined);

  return (
    <div
      className={classNames(css.control, className)}
      style={width ? { width } : maxWidth ? { maxWidth } : undefined}
      ref={ref}
    >
      <button
        type="button"
        className={classNames(css.control__button, { [css.control__buttonOpen]: open })}
        style={height ? { height } : undefined}
        onClick={() => setOpen(!open)}
      >
        <span
          className={classNames(css.control__value, {
            [css.control__valueCompact]: compact,
            [css.control__valuePlaceholder]: showPlaceholder,
          })}
        >
          {label}
        </span>
        <SVG
          icon={open ? 'iconCaretUp' : 'iconCaretDown'}
          className={classNames(css.control__caret, {
            [css.control__caretPlaceholder]: showPlaceholder,
          })}
        />
      </button>
      {!open ? null : (
        <div className={css.panel}>
          <div className={classNames(css.panel__list, menuClassName)}>
            {options.map((option) => (
              <button
                type="button"
                key={option.value}
                className={classNames(css.panel__item, {
                  [css.panel__itemCheckbox]: multiple,
                  [css.panel__itemSelected]: !multiple && isSelected(option),
                  [itemSelectedClassName]: itemSelectedClassName && isSelected(option),
                })}
                onClick={handleSelect(option)}
              >
                {!multiple ? null : <Checkbox checked={isSelected(option)} />}
                <span className={css.panel__itemLabel}>{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
